import { Component, Input, ViewChild, SimpleChanges, OnInit, OnChanges } from '@angular/core';
import { Router } from '@angular/router';
import { Message, MassCommService, CommunicationRecordSearch } from '../../shared/index';
import { ContactTypeService } from '../../../contacts/contact-type/shared/contact-type.service';
import { ContactType } from '../../../contacts/contact-type/shared/contact-type.model';
import { NameValue } from '../../../shared/model/name-value.model';
import { ScheduleModeEnum } from '../../shared/schedule-mode.enum';
import { NotificationService, UtilService } from '../../../shared/index';
import { TranslationService } from '../../../shared/translation/translation.service';
import { CronBuilderComponent } from 'src/app/shared/widgets/cron-builder/cron-builder.component';

/**
 * prep mass comm message for sending
 */
@Component({
  selector: 'wl-mass-comm-message-edit-send',
  templateUrl: 'mass-comm-message-edit-send.component.html',
  styleUrls: [],
  providers: [],
    standalone: false
})
export class MassCommMessageEditSendComponent implements OnInit, OnChanges {
  @Input() public message: Message = null;
  @Input() public readonly: boolean = false;
  @Input() public isDraft: boolean = false;
  @Input() public isResend: boolean = false;
  @Input() public userEmailAddress: string = '';
  /** writes the message editor's inlined-style HTML into message.Message before it is sent */
  @Input() public flushMessageEditor: () => Promise<void> = () => Promise.resolve();
  @ViewChild(CronBuilderComponent) public _cronBuilder: CronBuilderComponent;
  public contactTypes: ContactType[] = [];
  public specialFields: NameValue[] = [];
  public specialFieldsInMessage: string[] = [];

  public loading = false;
  public delayedSend: boolean = null;
  public sendingTestMessage: boolean = false;
  public sendingMessage: boolean = false;
  public scheduleModeOptions: object = ScheduleModeEnum;
  public enableUnsub = false;
  public displayEndDate = false;
  public displayUpcomingSendDate = false;

  /**
   * break apart date and time
   */
  public get dateAndTimeToSendDate(): Date {
    return this._dateAndTimeToSendDate;
  }
  public set dateAndTimeToSendDate(value: Date) {
    this._dateAndTimeToSendDate = value;

    if (!value) return;

    this.message.DateAndTimeToSend = this.mergeDateTime(value, this._dateAndTimeToSendTime);
  }

  /**
   * break apart date and time
   */
  public get dateAndTimeToSendTime(): string {
    return this._dateAndTimeToSendTime;
  }
  public set dateAndTimeToSendTime(value: string) {
    this._dateAndTimeToSendTime = value;

    if (!value) return;

    this.message.DateAndTimeToSend = this.mergeDateTime(this._dateAndTimeToSendDate, value);
  }

  private _dateAndTimeToSendDate: Date = new Date();
  private _dateAndTimeToSendTime: string = '';

  /**
   * inject
   */
  public constructor(
    private _massCommService: MassCommService,
    private _contactTypeService: ContactTypeService,
    private _router: Router,
    private _notificationService: NotificationService,
    private _translationService: TranslationService,
    private _utilService: UtilService
  ) {}

  /**
   * my initer
   */
  public ngOnInit(): void {
    this.initComponent();
  }

  /**
   * load data
   */
  private async initComponent(): Promise<void> {
    this.loading = true;
    try {
      let contactTypesPromise = this._contactTypeService
        .getContactTypes({ PageNumber: 0, PageSize: 0, OrderBy: 'ContactType' })
        .then((contactTypes) => {
          if (contactTypes && contactTypes.Result) this.contactTypes = contactTypes.Result;
        });

      let specialFieldsPromise = this._massCommService.getEmailSpecialFields().then((specialFields) => {
        if (specialFields) this.specialFields = specialFields;
      });
      // the dateandtimetosend gets overwritten later if this property isn't set.
      if (this.message) {
        this.dateAndTimeToSendDate = this.message.DateAndTimeToSend;
        this.enableUnsub = this.message.UnsubscribeEmailAddress != null && this.message.UnsubscribeEmailAddress != '';
      }
      await Promise.all([contactTypesPromise, specialFieldsPromise]);
    } catch (e) {
    } finally {
      this.loading = false;
    }
    this.displayEndDate = this.message.EndDate != null;
  }

  /**
   * when an @Input is changed. fires after property is updated
   * @param change
   */
  public ngOnChanges(change: SimpleChanges): void {
    if (change['message'] && this.message && change['message'].previousValue != this.message) {
      if (this.message.Recurring) {
        this.message.ScheduleMode = ScheduleModeEnum.Recurring;
        this.displayUpcomingSendDate = JSON.stringify(this.message.UpcomingSendDate) != JSON.stringify(this.message.DateAndTimeToSend);
      } else if (this.message.DateAndTimeToSend >= new Date()) {
        this.message.ScheduleMode = ScheduleModeEnum.DelaySend;
      }

      if (!this.message.MessageSent) {
        if (!this.message.DateAndTimeToSend) {
          this.message.DateAndTimeToSend = new Date();
        }

        this._dateAndTimeToSendDate = this.message.DateAndTimeToSend;

        let hours24 = this.message.DateAndTimeToSend.getHours();
        let minutes = this.message.DateAndTimeToSend.getMinutes();

        const { hour, minute, meridian } = this.convertTime24hTo12h(hours24, minutes);

        let minutesStr: string = (minute < 10 ? '0' : '') + minute;

        this._dateAndTimeToSendTime = hour + ':' + minutesStr + ' ' + meridian;
      }
    }
  }

  endDateChanged($event) {
    if (!$event) {
      this.message.EndDate = null;
    }
  }
  /**
   * trigger a read of the messages Mesage and Subject for special fields.
   * Updates the UI for the alternate values.
   * must actively call this, rather than constantly passively re-evaluating the message when the user may never even see this screen.
   */
  public evaluateMessageForSpecialFields() {
    if (!this.message || !this.message.Message) return;

    // see if they're using [[something]] fields
    let tagsInMessage: string[] = this.getTagsUsedInMessage();

    this.specialFieldsInMessage = [];

    if (!tagsInMessage || !tagsInMessage.length) return;

    let tagsInMessageLower: string[] = tagsInMessage.map((x) => x.toLocaleLowerCase());

    // match the strings from the message to alt fields
    // no alternatives:
    // unsubscribelink, businessconnectrequestlink, profileupdatelink, affiliationupdatelink, encryptedprofileid, mailingaddress, billingaddress
    let altFieldNames: string[] = Object.keys(this.message).filter((x) => x.startsWith('ALT_'));
    let specialFieldsSpecialSpelling: { [lowerCaseMatch: string]: string } = {
      alt_fname: 'firstname',
      alt_lname: 'lastname',
      alt_duesanchormonth: 'duesmonth',
      alt_customfield: 'customfield_id',
      alt_noofunits: 'numberofunits',
    };

    // go in order of fields on the message object so it's always the same order
    for (var i = 0; i < altFieldNames.length; i++) {
      let altFieldName: string = altFieldNames[i];

      // already got it
      if (this.specialFieldsInMessage.indexOf(altFieldName) >= 0) continue;

      let altFieldNameLower: string = altFieldNames[i].toLowerCase();
      let altFieldTagName: string = '';

      // figure out tag spelling for this field (alt_reportname = [[reportname]]  or  alt_fname = [[firstname]])
      if (specialFieldsSpecialSpelling[altFieldNameLower]) {
        // this field has a special spelling (alt_fname maps to [[firstname]])
        altFieldTagName = specialFieldsSpecialSpelling[altFieldNameLower];
      } else {
        // field spelled the same but with alt_ in front of tag
        altFieldTagName = altFieldNameLower.replace(/^alt_/, '');
      }

      // check if the tag occurs in the message
      var foundIndex: number = tagsInMessageLower.indexOf(altFieldTagName);

      if (foundIndex > -1) this.specialFieldsInMessage.push(altFieldName);
    }
  }

  /**
   * see if they're using [[something]] fields
   */
  private getTagsUsedInMessage(): string[] {
    if (!this.message && !this.message.Message) return [];

    let specialFieldsPattern = /\[\[(\w+)(?:=[^\]]+)?\]\]/g;

    // see if they're using [[something]] fields
    let tagsInMessage: string[] = [];
    let match: RegExpExecArray = null;

    while ((match = specialFieldsPattern.exec(this.message.Message))) {
      if (match && match.length > 1) tagsInMessage.push(match[1]);
    }

    if (!tagsInMessage || !tagsInMessage.length) return [];

    return tagsInMessage;
  }

  /**
   * delayed send checkbox changed
   */
  public handleDelaySendClick(): void {
    if (this.message.ScheduleMode != ScheduleModeEnum.DelaySend) {
      this.message.DateAndTimeToSend = new Date();
      this.dateAndTimeToSendDate = new Date();
      this.dateAndTimeToSendTime = '12:00 Midnight';
    }
  }

  /**
   * the checkbox for this field is the opposite
   * @param newValue
   */
  public handleEliminateDupEmailsClick(newValue: boolean): void {
    this.message.EliminateDupEmails = newValue;
  }

  public toggleUnsub(newValue: boolean) {
    if (!newValue) {
      this.message.UnsubscribeEmailAddress = null;
    }
    this.enableUnsub = newValue;
  }

  /**
   * send test message to email
   */
  public async handleSendTestMessageClick(): Promise<void> {
    if (this.sendingTestMessage) return;

    this.sendingTestMessage = true;

    try {
      await this.flushMessageEditor();
      let profileId: number = await this.getFirstRecipientProfileId();

      await this._massCommService.sendTestMessage(this.message, this.userEmailAddress, profileId);

      this._notificationService.success(
        this._translationService.getInstant('communications.mass-comm-message-edit.mass-comm-send.test-email-sent-success')
      );
    } catch (error) {
      this._notificationService.httpError(error);
    } finally {
      this.sendingTestMessage = false;
    }
  }

  /**
   * user wants to send the mass comm. this is the final send.
   */
  public async handleSendClick(): Promise<void> {
    if (this.sendingMessage) return;

    this.sendingMessage = true;

    if (
      this.enableUnsub &&
      this.message.UnsubscribeEmailAddress &&
      this.message.UnsubscribeEmailAddress.trim() &&
      !this._utilService.isValidEmail(this.message.UnsubscribeEmailAddress)
    ) {
      this._notificationService.error(
        this._translationService.getInstant('communications.mass-comm-message-edit.mass-comm-send.unsub-invalid-email')
      );
      this.sendingMessage = false;
      return;
    }

    // not a draft id
    try {
      await this.flushMessageEditor();
      let newMessageId: number = await this._massCommService.send(this.message);

      this.message.MessageSent = true;
      this._notificationService.persistentSuccess(
        this._translationService.getInstant('communications.mass-comm-message-edit.mass-comm-send.send-immediately-success')
      );

      switch (this.message.ScheduleMode) {
        case ScheduleModeEnum.Recurring:
          this._router.navigate(['communications/message-admin/recurring']);
          break;
        case ScheduleModeEnum.DelaySend:
          this._router.navigate(['communications/message-admin/scheduled']);
          break;
        default:
          this._router.navigate(['communications/message-admin/sent']);
          break;
      }
    } catch (e) {
    } finally {
      this.sendingMessage = false;
    }
  }

  public async handleSendRecurringClick(): Promise<void> {
    if (this.sendingMessage) return;

    this.sendingMessage = true;
    await this.flushMessageEditor();
    let messageClone = this._utilService.clone(this.message) as Message;
    messageClone.CronExpression = this._cronBuilder.convertLocalCronExpressionToUtc();
    try {
      // always save as draft when saving unless already scheduled or "send" clicked
      if (this.message.MessageId <= 0) {
        this._massCommService.createDraftMessage(messageClone).then((id: number) => {
          this._router.navigate(['communications/message-admin/recurring']);
        });
      } else {
        this._massCommService.updateDraftMessage(messageClone);
        this._translationService.getTitlecase('communications.mass-comm-message-admin.message-saved').then((message: string) => {
          this._notificationService.success(message);
        });
      }

      this.message.MessageSent = true;
    } catch (e) {
    } finally {
      this.sendingMessage = false;
    }
  }

  /**
   * get first profileid from list of recipients
   */
  private getFirstRecipientProfileId(): Promise<number> {
    let profileIdPromise: Promise<number> = Promise.resolve(0);

    if (this.isDraft || this.message.MessageId <= 0) {
      if (this.message.ProfileSearchParams) {
        // profile search
        profileIdPromise = this._massCommService.searchDraftProfiles(this.message.ProfileSearchParams).then((result) => {
          if (result && result.Result && result.Result.length > 0) return result.Result[0].ProfileId;

          return 0;
        });
      }
    } else if (this.message.MessageId > 0) {
      var search = new CommunicationRecordSearch();
      search.MessageId = this.message.MessageId;
      search.PageNumber = 1;
      search.PageSize = 1;

      profileIdPromise = this._massCommService.searchCommunicationRecords(search).then((result) => {
        if (result && result.Result && result.Result.length > 0) return result.Result[0].ProfileId;

        return 0;
      });
    }

    return profileIdPromise;
  }

  /**
   * helper method to rebuild datetime
   * @param date
   * @param time
   */
  private mergeDateTime(date: Date, time: string): Date {
    if (!date) return;

    let newDate = new Date(date);

    if (!time) return newDate;

    let [hourStr, minuteStr, meridian] = time.split(/[: ]+/);
    let hour = parseInt(hourStr);
    let minute = parseInt(minuteStr);

    const { hours24, minutes: mins } = this.convertTime12hTo24h(hour, minute, meridian);

    newDate.setHours(hours24, mins, 0, 0);

    return newDate;
  }

  private convertTime12hTo24h(hour: number, minute: number, meridian: string): { hours24: number; minutes: number } {
    if (!meridian) {
      return { hours24: hour, minutes: minute };
    }

    meridian = meridian.toLowerCase();

    if (meridian === 'midnight') {
      hour = 0;
    } else if (meridian === 'noon') {
      hour = 12;
    } else if (meridian === 'am') {
      if (hour === 12) {
        hour = 0;
      }
    } else if (meridian === 'pm') {
      if (hour < 12) {
        hour += 12;
      }
    }

    return { hours24: hour, minutes: minute };
  }

  private convertTime24hTo12h(hours24: number, minutes: number): { hour: number; minute: number; meridian: string } {
    let hour: number;
    let meridian: string;

    if (hours24 === 0) {
      hour = 12;
      meridian = 'AM';
    } else if (hours24 < 12) {
      hour = hours24;
      meridian = 'AM';
    } else if (hours24 === 12) {
      hour = 12;
      meridian = 'PM';
    } else {
      hour = hours24 - 12;
      meridian = 'PM';
    }

    return { hour, minute: minutes, meridian };
  }
}
